import React from 'react';

type LinkifiedTextProps = {
  text: string;
  className?: string;
};

const URL_RE = /\b(?:https?:\/\/|www\.)[^\s<]+/gi;
const EMAIL_RE = /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi;
const MARKDOWN_LINK_RE = /\[([^\]\n]+)\]\(([^)\s]+)\)/g;
const TRAILING_PUNCTUATION_RE = /[.,;:!?)\]}]+$/;

function splitTrailingPunctuation(value: string) {
  const match = value.match(TRAILING_PUNCTUATION_RE);
  if (!match) return { url: value, trailing: '' };

  return {
    url: value.slice(0, -match[0].length),
    trailing: match[0]
  };
}

function normalizeHref(rawHref: string) {
  const href = rawHref.trim();
  if (/^www\./i.test(href)) return `https://${href}`;
  if (/^(https?:|mailto:)/i.test(href) || href.startsWith('/')) return href;
  return null;
}

function linkProps(href: string) {
  if (/^https?:/i.test(href)) {
    return { target: '_blank', rel: 'noreferrer' };
  }
  return {};
}

function displayUrlLabel(rawUrl: string, href: string) {
  if (!/^https?:/i.test(href)) return rawUrl;

  try {
    const url = new URL(href);
    const hasPath = url.pathname && url.pathname !== '/';
    if (hasPath || url.search || url.hash) return url.hostname;
    return `${url.hostname}${url.pathname === '/' ? '' : url.pathname}`;
  } catch {
    return rawUrl.replace(/^https?:\/\//i, '').replace(/\/$/, '');
  }
}

const LINK_CLASS_NAME = 'font-medium text-red-700 underline decoration-red-300 underline-offset-2 hover:text-red-800';

export function LinkifiedText({ text, className }: LinkifiedTextProps) {
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  const linkMatches = [
    ...Array.from(text.matchAll(MARKDOWN_LINK_RE), (match) => ({
      type: 'markdown' as const,
      match,
      index: match.index ?? 0
    })),
    ...Array.from(text.matchAll(URL_RE), (match) => ({
      type: 'auto' as const,
      match,
      index: match.index ?? 0
    })),
    ...Array.from(text.matchAll(EMAIL_RE), (match) => ({
      type: 'email' as const,
      match,
      index: match.index ?? 0
    }))
  ].sort((a, b) => a.index - b.index || (a.type === 'markdown' ? -1 : 1));

  for (const { type, match, index } of linkMatches) {
    if (index < lastIndex) continue;

    const rawValue = match[0];

    if (index > lastIndex) {
      parts.push(text.slice(lastIndex, index));
    }

    if (type === 'markdown') {
      const label = match[1];
      const href = normalizeHref(match[2]);

      if (href) {
        parts.push(
          <a
            key={`${index}-${href}`}
            href={href}
            {...linkProps(href)}
            className={LINK_CLASS_NAME}
            onClick={(event) => event.stopPropagation()}
          >
            {label}
          </a>
        );
      } else {
        parts.push(rawValue);
      }

      lastIndex = index + rawValue.length;
      continue;
    }

    const { url, trailing } = splitTrailingPunctuation(rawValue);

    if (url) {
      const href = type === 'email' ? `mailto:${url}` : normalizeHref(url);

      if (href) {
        const label = type === 'email' ? url : displayUrlLabel(url, href);
        parts.push(
          <a
            key={`${index}-${url}`}
            href={href}
            {...linkProps(href)}
            className={LINK_CLASS_NAME}
            onClick={(event) => event.stopPropagation()}
          >
            {label}
          </a>
        );
      } else {
        parts.push(url);
      }
    }

    if (trailing) {
      parts.push(trailing);
    }

    lastIndex = index + rawValue.length;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return <span className={className}>{parts}</span>;
}
